// The construct slice of the stem ViewModel: the full set of buildable stem constructs and
// the currently selected stem/head, read from and written back to the planner Model.
import { computed } from 'vue';
import type { PlannerStore } from '@/views/3d-planner/store/plannerStore.types';
import type { Url } from '@/domain/types';
import { getCatalogComponent } from '@/domain/catalog/catalog';
import { computeStem } from '@/views/3d-planner/fitting/componentUrls';
import { formatNumberSign } from '@/util/format/formatNumberSign';
import type { StemSystem } from '@/domain/catalog/stem';
import { buildFittedStemConstructs } from '@/views/3d-planner/stem/stemServices';
import { buildSelectedComponentInfo } from '@/views/3d-planner/stem/selection/stemSelectableItems';
import type {
    SelectableHeadOffset,
    SelectableStem,
    SelectedComponentInfo,
} from '@/views/3d-planner/stem/stem.types';

export const useStemConstructs = (planner: PlannerStore) => {
    const allStemConstructs = computed<SelectableStem[]>(() =>
        planner.fittedComponents && planner.catalog
            ? buildFittedStemConstructs(
                  planner.fittedComponents.stems,
                  planner.catalog.stems,
                  planner.catalog.heads,
              )
            : [],
    );

    const selectedStem = computed<Url | undefined>({
        get: () => planner.template?.stemUrl,
        set: (value?: Url) => {
            if (value && selectedStem.value !== value) {
                planner.setStem(value);
            }
        },
    });

    const selectedHead = computed<SelectableHeadOffset | null>({
        get: () => {
            if (!planner.template || !planner.catalog) {
                return null;
            }
            const head = getCatalogComponent(planner.catalog.heads, planner.template.headUrl);
            return { text: formatNumberSign(head.offset), headUrl: head.self };
        },
        set: (value: SelectableHeadOffset | null) => {
            if (value?.headUrl && selectedHead.value?.headUrl !== value.headUrl) {
                planner.setHead(value.headUrl);
            }
        },
    });

    const info = computed<SelectedComponentInfo | null>(() =>
        planner.template && planner.catalog
            ? buildSelectedComponentInfo(
                  planner.catalog.stems,
                  planner.catalog.heads,
                  planner.template,
              )
            : null,
    );

    const preferredSystem = computed<StemSystem | null>(
        () => planner.case?.specification?.preferredSystem ?? null,
    );

    const isSelectedStemSystemPreferred = computed<boolean>(() =>
        selectedStem.value && preferredSystem.value && planner.catalog
            ? computeStem(planner).system === preferredSystem.value
            : false,
    );

    return {
        allStemConstructs,
        selectedStem,
        selectedHead,
        info,
        preferredSystem,
        isSelectedStemSystemPreferred,
    };
};

export type StemConstructs = ReturnType<typeof useStemConstructs>;